/**
 * 图表辅助工具函数
 */

/**
 * 根据数据计算动态的 Y 轴范围
 * @param data 数据数组，可以是数字数组或包含数字值的对象数组
 * @param options 配置选项
 * @returns 返回 Y 轴的最小值和最大值
 */
export interface DynamicYAxisOptions {
  /** 数据字段名，如果数据是对象数组 */
  valueField?: string;
  /** 最小边距百分比，默认 0.1 (10%) */
  minMargin?: number;
  /** 最大边距百分比，默认 0.1 (10%) */
  maxMargin?: number;
  /** 强制最小值，如果计算出的最小值大于此值，则使用此值 */
  forceMin?: number;
  /** 强制最大值，如果计算出的最大值小于此值，则使用此值 */
  forceMax?: number;
  /** 是否向上取整到整数，默认 true */
  roundToInteger?: boolean;
  /** 最小范围，确保 max - min 至少为此值，默认 10 */
  minRange?: number;
}

export function calculateDynamicYAxis(
  data: (number | any)[] | (number | any)[][],
  options: DynamicYAxisOptions = {}
): { min: number; max: number } {
  const {
    valueField,
    minMargin = 0.1,
    maxMargin = 0.1,
    forceMin,
    forceMax,
    roundToInteger = true,
    minRange = 10,
  } = options;

  // 扁平化数据并提取数值
  const flatData: number[] = [];

  const processData = (item: any) => {
    if (typeof item === "number") {
      flatData.push(item);
    } else if (Array.isArray(item)) {
      item.forEach(processData);
    } else if (typeof item === "object" && item !== null) {
      if (valueField && typeof item[valueField] === "number") {
        flatData.push(item[valueField]);
      } else {
        // 如果没有指定字段，尝试找到所有数字属性
        Object.values(item).forEach((value) => {
          if (typeof value === "number") {
            flatData.push(value);
          }
        });
      }
    }
  };

  if (Array.isArray(data)) {
    data.forEach(processData);
  }

  // 过滤掉非法值
  const validData = flatData.filter((val) => !isNaN(val) && isFinite(val));

  if (validData.length === 0) {
    return { min: forceMin ?? 0, max: forceMax ?? 100 };
  }

  // 计算基础最小值和最大值
  const dataMin = Math.min(...validData);
  const dataMax = Math.max(...validData);

  // 如果所有数据都相同，创建一个合理的范围
  if (dataMin === dataMax) {
    const center = dataMin;
    const range = Math.max(minRange, Math.abs(center) * 0.2);
    return {
      min: Math.max(0, center - range / 2),
      max: center + range / 2,
    };
  }

  // 计算数据范围
  const dataRange = dataMax - dataMin;

  // 计算边距
  const minMarginValue = dataRange * minMargin;
  const maxMarginValue = dataRange * maxMargin;

  // 计算初始的最小值和最大值
  let calculatedMin = dataMin - minMarginValue;
  let calculatedMax = dataMax + maxMarginValue;

  // 应用强制限制
  if (forceMin !== undefined) {
    calculatedMin = Math.min(calculatedMin, forceMin);
  }
  if (forceMax !== undefined) {
    calculatedMax = Math.max(calculatedMax, forceMax);
  }

  // 确保最小范围
  const currentRange = calculatedMax - calculatedMin;
  if (currentRange < minRange) {
    const center = (calculatedMax + calculatedMin) / 2;
    calculatedMin = center - minRange / 2;
    calculatedMax = center + minRange / 2;
  }

  // 确保最小值不小于 0（对于百分比数据）
  if (calculatedMin < 0 && dataMin >= 0) {
    calculatedMin = 0;
  }

  // 向上取整到整数
  if (roundToInteger) {
    calculatedMin = Math.floor(calculatedMin);
    calculatedMax = Math.ceil(calculatedMax);
  }

  return {
    min: calculatedMin,
    max: calculatedMax,
  };
}

/**
 * 从图表系列数据中提取所有数值
 * @param series 图表系列数据
 * @returns 所有数值的数组
 */
export function extractSeriesValues(series: any[]): number[] {
  const values: number[] = [];

  series.forEach((s) => {
    if (s.values && Array.isArray(s.values)) {
      s.values.forEach((val: any) => {
        if (typeof val === "number") {
          values.push(val);
        }
      });
    } else if (s.data && Array.isArray(s.data)) {
      s.data.forEach((val: any) => {
        if (typeof val === "number") {
          values.push(val);
        }
      });
    }
  });

  return values;
}

/**
 * 为百分比图表计算合适的 Y 轴范围
 * @param series 图表系列数据
 * @param options 额外选项
 */
export function calculatePercentageYAxis(
  series: any[]
  // options: Partial<DynamicYAxisOptions> = {}
): { min: number; max: number } {
  const values = extractSeriesValues(series);

  if (values.length === 0) {
    return { min: 0, max: 100 };
  }

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);

  // 智能选择整齐的边界值
  const smartMin = getSmartBoundary(dataMin, "min");
  const smartMax = getSmartBoundary(dataMax, "max");

  // 确保最小范围为 20%
  const range = smartMax - smartMin;
  if (range < 20) {
    const center = (smartMax + smartMin) / 2;
    return {
      min: Math.max(0, center - 10),
      max: Math.min(100, center + 10),
    };
  }

  // 优化边界显示：只有当数据接近边界时才显示0%或100%
  let finalMin = smartMin;
  let finalMax = smartMax;

  // 如果数据最小值距离0%较远（>10%），则不强制显示0%
  if (dataMin > 10) {
    finalMin = smartMin;
  } else {
    finalMin = Math.max(0, smartMin);
  }

  // 如果数据最大值距离100%较远（<90%），则不强制显示100%
  if (dataMax < 90) {
    finalMax = smartMax;
  } else {
    finalMax = Math.min(100, smartMax);
  }

  return {
    min: finalMin,
    max: finalMax,
  };
}

/**
 * 获取智能边界值
 * @param value 原始值
 * @param type 边界类型：'min' 向下取整，'max' 向上取整
 */
function getSmartBoundary(value: number, type: "min" | "max"): number {
  // 确定合适的步长
  let step: number;

  if (value <= 10) {
    step = 1; // 0-10 使用 1 的步长
  } else if (value <= 50) {
    step = 5; // 10-50 使用 5 的步长
  } else {
    step = 10; // 50+ 使用 10 的步长
  }

  if (type === "min") {
    // 向下取整到最近的步长倍数
    return Math.floor(value / step) * step;
  } else {
    // 向上取整到最近的步长倍数
    return Math.ceil(value / step) * step;
  }
}
